import { ApiClientError } from "@gettactic/api";
import { delegatedPermissionPartials } from "@gettactic/helpers";
import pluralize from "pluralize";

export const DELEGATED_ACCESS_REQUIRED = "delegated_access_required";

/**
 * Reservation errors the API rejects outright, as opposed to availability errors
 * which come back per slot. Most describe an impossible mix of resources, which only
 * multi-resource reservations can produce.
 */
export const reservationErrorMessagesByCode: Record<string, string> = {
  mixed_resource_types:
    "A reservation can't mix different resource types. Please book them separately.",
  too_many_resources:
    "Only meeting rooms can be booked several at a time. Please remove the extra resource.",
  inconsistent_slot_users:
    "Everyone on this reservation has to be on all of its rooms. Please try again.",
  inconsistent_group_attendees:
    "Everyone on this reservation has to be on all of its rooms. Please try again.",
  inconsistent_group_resource_types:
    "The selected rooms can't be booked together. Please remove one of them.",
  resource_ticket_only:
    "This resource can only be booked through a workplace request.",
  [DELEGATED_ACCESS_REQUIRED]: delegatedPermissionPartials.requiredError
};

/**
 * Whether a failed reservation request was rejected because the organizer hasn't
 * granted Outlook delegated access.
 */
export const isDelegatedAccessRequiredError = (error: unknown): boolean => {
  if (!(error instanceof ApiClientError)) {
    return false;
  }
  const result: unknown = error.response?.result;
  if (typeof result !== "object" || result === null || !("error" in result)) {
    return false;
  }
  const apiError: unknown = result.error;
  return (
    typeof apiError === "object" &&
    apiError !== null &&
    "code" in apiError &&
    apiError.code === DELEGATED_ACCESS_REQUIRED
  );
};

export interface RoomsOverCapacityMessageParams {
  /** Rooms whose capacity was checked as one, straight from `group_resource_ids`. */
  groupResourceIds?: string[];
  /** Combined capacity of those rooms. */
  totalSlots?: number;
  /** Pre-rendered " on Jan 1, Jan 2" fragment, or an empty string. */
  affectedDatesText: string;
}

/**
 * Message for an over-capacity failure on rooms booked together. Their capacity is
 * checked as one pool, so the failure is about the group rather than any single room.
 * Returns null when the failure was not a grouped one — the caller then falls back to
 * the single-resource wording.
 */
export const getRoomsOverCapacityMessage = ({
  groupResourceIds,
  totalSlots,
  affectedDatesText
}: RoomsOverCapacityMessageParams): string | null => {
  const groupSize = groupResourceIds?.length ?? 0;
  if (groupSize < 2) {
    return null;
  }

  const capacityText = totalSlots
    ? ` (${totalSlots} ${pluralize("seat", totalSlots)})`
    : "";

  return `Could not complete the reservation${affectedDatesText}, because the ${groupSize} selected rooms together${capacityText} can't hold everyone.`;
};
